import { IonButton, IonInput, IonItem, IonLabel, IonSelect, IonSelectOption, IonText, IonTextarea } from "@ionic/react";
import { FormEvent, useRef, useState } from "react";
import { Assignment } from "../../../data-types/Types";
import { useData } from "../services/DataProvider";
import AssignmentScheduleFields from "./AssignmentScheduleFields";
import { buildUtcMsFromLocalParts } from "../utils/assignmentAvailability";

const CreateAssignment:React.FC<{classID:string}> = ({classID, ...props})=>{
  const [error, setError] = useState("");
  const {synching, createOwnedRecord} = useData();
  const quizSelectRef = useRef<HTMLIonSelectElement>(null);
  const titleRef = useRef<HTMLIonInputElement>(null);
  const descriptionRef = useRef<HTMLIonTextareaElement>(null);
  const [scheduleFields, setScheduleFields] = useState({
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
  });

  const topicQuizIDs = {
    "4": {
      "I. Earning Income": "yfUPIKYDK7Q7gPZhhwIW",
      "II. Spending": "w6Ky1miYyeDyqL2q2d2H", 
      "III. Saving": "ood6qfCWAcwbyr6SCcQi",
      "IV. Investing": "FrBvtLW5pvFm9E6goBzi",
      "V. Managing Credit": "EUDCyCe7rOGgi9KkpWKt",
      "VI. Managing Risk": "5w9Pm74NlFSOewcu2xsj"
    },
    "8": {
      "I. Earning Income": "hFo5y7X3pli2iXOE7qqB",
      "II. Spending": "QARrHEfsOBTIzEw8Ishw", 
      "III. Saving": "vD8j3IWPJ7WdYDMGryOe",
      "IV. Investing": "n48EFoQwWhUf39RDOGRo",
      "V. Managing Credit": "iTB9H61oiEiNTY07s6A3",
      "VI. Managing Risk": "SiXY5Rj4ic690lxzsLtN"
    },
    "12": {
      "I. Earning Income": "ZmQZtYyOu6zCmqKy0oNF",
      "II. Spending": "wflAiJqOFTA7IGSMU0BU", 
      "III. Saving": "sshfwH3eNEcEGmblusDD",
      "IV. Investing": "T4Ki1Jj4hKFcwwgPtnyu",
      "V. Managing Credit": "KLlJ4vaBGlpzDKe1XRMt",
      "VI. Managing Risk": "oxZy6OAohlpwbRXByi9E"
    },
  };

  const cumulativeQuizzes = {
    "Comprehensive Quiz (4th Grade Standards)": "p6XUren59FKRZ0v83BNN",
    "Comprehensive Quiz (8th Grade Standards)": "x983lNKskSgoYRibD9dg",
    "Comprehensive Quiz (All Grades Pre-Test/Post-Test)": "CQLSM4uOiYtbtWfCLqKS"
  };

  // const allowedQuizzes = {
  //   // "Comprehensive Quiz (4th Grade Standards)": "p6XUren59FKRZ0v83BNN",
  //   "High School Investing": "T4Ki1Jj4hKFcwwgPtnyu",
  // }

  const allowedQuizzes = {
    // ...topicQuizIDs["8"],
    // "Comprehensive Quiz (8th Grade Standards)": "x983lNKskSgoYRibD9dg",
    "FLY Pre-Test": "TzOLB9IFxnFnPLfh630x",
    "FLY Careers Test": "MMMmELWSM91TtjRG0qGa",
    "FLY Life After High School (Earning+Spending)": "SMxZGYuzdlLiI2hQ8swq",
    "FLY 8th-Grade Consumer Skills (Spending)": "4E0r0DxkaoZjCo4Rhkmd",
    "FLY 8th-Grade Credit Quiz": "xl3toks5HGPXNUpItfaY",
    "FLY Investing Quiz": "RJZoIbF7LTo2UAEEYzaV",
    "FLY Post-Test": "LJLLC9tGl0HIS7N4qYll",
  }

  async function handleSubmit(event:FormEvent) {
    event.preventDefault(); // Prevents page from refreshing on form submit.
    // TODO: clear fields if successful.
    setError("");

    const assignmentTitle = "" + titleRef.current?.value;
    const assignmentDescription = "" + descriptionRef.current?.value;

    const assignment: Assignment = {
      classCode: classID,
      quizID: quizSelectRef.current?.value || "",
      title: assignmentTitle,
      description: assignmentDescription,
      requiredQuestions: 20,
    }

    const startAt = buildUtcMsFromLocalParts(scheduleFields.startDate, scheduleFields.startTime, "start");
    const endAt = buildUtcMsFromLocalParts(scheduleFields.endDate, scheduleFields.endTime, "end");

    if (startAt.utcMs !== undefined) {
      assignment.startAtUtcMs = startAt.utcMs;
      assignment.startAtHasTime = startAt.hasTime;
    }

    if (endAt.utcMs !== undefined) {
      assignment.endAtUtcMs = endAt.utcMs;
      assignment.endAtHasTime = endAt.hasTime;
    }

    if (assignment.quizID.length <= 0) {
      setError("Please select a quiz to assign.");
    } else if (assignmentTitle.trim().length <= 1) {
      setError("Please give the assignment a descriptive title. (For example, is this a post-test, a pre-test, a topical test, or just a review activity?)");
    } else if (assignment.startAtUtcMs && assignment.endAtUtcMs && assignment.startAtUtcMs > assignment.endAtUtcMs) {
      setError("Start date/time must be before the end date/time.");
    } else {
      createOwnedRecord("finlitri-assignments", assignment).catch((reason) => {
        setError('Failed to create assignment: ' + (reason.message || ""));
      })
    }
  }

  return <form onSubmit={handleSubmit}>
    {error.length > 0? <IonText color="danger">{error}</IonText>: <></>}
    <IonItem>
      <IonLabel>Quiz to Assign</IonLabel>
      <IonSelect ref={quizSelectRef} style={{"maxWidth":"100%"}}>
        {Object.keys(allowedQuizzes).map((quiz, index)=>{return <IonSelectOption key={index} style={{"maxWidth":"100%"}} value={allowedQuizzes[quiz as keyof typeof allowedQuizzes]}>{quiz}</IonSelectOption>})}
      </IonSelect>
    </IonItem>
    <IonItem>
      <IonLabel position="floating">Assignment Name</IonLabel>
      <IonInput type="text" ref={titleRef}/>
    </IonItem>
    <IonItem>
      <IonLabel position="floating">Description</IonLabel>
      <IonTextarea ref={descriptionRef} autoGrow/>
    </IonItem>
    <AssignmentScheduleFields
      {...scheduleFields}
      onChange={setScheduleFields}
    />
    <IonButton expand="block" type="submit" className="ion-margin-top" disabled={synching}>
      Create
    </IonButton>
  </form>
}

export default CreateAssignment;